import React, { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import AdminSearch from '../../components/admin/AdminSearch';

const statusColor = {
  placed: 'bg-amber-100 text-amber-800 border-amber-300',
  confirmed: 'bg-blue-100 text-blue-800 border-blue-300',
  processing: 'bg-indigo-100 text-indigo-800 border-indigo-300',
  shipped: 'bg-purple-100 text-purple-800 border-purple-300',
  delivered: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  cancelled: 'bg-rose-100 text-rose-800 border-rose-300',
  returned: 'bg-gray-100 text-gray-800 border-gray-300',
};

const allStatuses = ['placed', 'confirmed', 'processing', 'shipped', 'delivered', 'cancelled', 'returned'];

export default function AdminOrders() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState({ items: [], pages: 1 });
  const [loading, setLoading] = useState(true);
  const search = searchParams.get('search') || '';
  const [searchInput, setSearchInput] = useState(search);
  const status = searchParams.get('status') || '';
  const page = Number(searchParams.get('page') || 1);

  const fetchOrders = useCallback(() => {
    setLoading(true);
    const params = { page, limit: 20 };
    if (status) params.status = status;
    if (search) params.search = search;
    api.get('/orders', { params }).then((res) => setData(res.data)).finally(() => setLoading(false));
  }, [status, search, page]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const updateStatusFilter = (val) => {
    const next = new URLSearchParams(searchParams);
    if (val) next.set('status', val); else next.delete('status');
    next.set('page', '1');
    setSearchParams(next);
  };

  const updateSearch = (value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set('search', value); else next.delete('search');
    next.set('page', '1');
    setSearchParams(next);
  };

  const handleQuickStatusChange = async (orderId, newStatus) => {
    try {
      await api.patch(`/orders/${orderId}/status`, { status: newStatus, note: `Status updated via Admin Table to ${newStatus}` });
      toast.success(`Order status changed to ${newStatus.toUpperCase()}`);
      fetchOrders();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to update order status');
    }
  };

  const exportCSV = () => {
    if (!data.items.length) {
      toast.error('No orders available to export');
      return;
    }
    const headers = ['Order Number', 'Customer Name', 'Distance (km)', 'Total Amount', 'Payment Method', 'Payment Status', 'Order Status', 'Date'];
    const rows = data.items.map((o) => [
      o.orderNumber,
      `"${o.user?.name || 'Customer'}"`,
      o.distanceKm || 0,
      o.totalAmount,
      o.paymentMethod,
      o.paymentStatus,
      o.orderStatus,
      new Date(o.createdAt).toLocaleDateString(),
    ]);

    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `Chandana_Aura_Orders_Report_${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Orders CSV Report downloaded');
  };

  return (
    <div className="space-y-6 text-[#2B1810]">
      <div className="bg-white border border-[#E8DFD8] rounded-xl p-6 shadow-sm flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#2B1810]">Customer Orders Management</h1>
          <p className="text-xs text-[#8A796C]">Track order status, distance logistics, and status updates.</p>
        </div>

        <div className="flex w-full xl:w-auto flex-col sm:flex-row sm:items-center gap-3">
          <AdminSearch value={searchInput} onChange={setSearchInput} onSearch={updateSearch} placeholder="Order, customer, phone or pincode" />
          <select className="bg-[#FAF8F5] border border-[#E0D6CC] text-xs font-semibold px-3 py-2 rounded focus:outline-none focus:border-[#C49A45]" value={status} onChange={(e) => updateStatusFilter(e.target.value)}>
            <option value="">All Order Statuses</option>
            {allStatuses.map((s) => (
              <option key={s} value={s}>{s.toUpperCase()}</option>
            ))}
          </select>

          <button
            onClick={exportCSV}
            className="bg-[#C49A45] hover:bg-[#B08736] text-white font-bold text-xs uppercase tracking-wider px-4 py-2 rounded shadow transition-colors flex items-center gap-1.5"
          >
            <span>↓ Export CSV Report</span>
          </button>
        </div>
      </div>

      <div className="bg-white border border-[#E8DFD8] rounded-xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-xs text-left">
            <thead className="bg-[#F5EFEB] text-[#2B1810] font-heading uppercase text-[11px] font-bold tracking-wider border-b border-[#E8DFD8]">
              <tr>
                <th className="p-4">Order Ref</th>
                <th className="p-4">Customer</th>
                <th className="p-4">Distance</th>
                <th className="p-4">Total Amount</th>
                <th className="p-4">Payment</th>
                <th className="p-4">Update Status</th>
                <th className="p-4">Date</th>
                <th className="p-4 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E8DFD8]">
              {loading ? (
                <tr><td className="p-6 text-center text-[#8A796C]" colSpan={8}>Loading customer orders...</td></tr>
              ) : data.items.length === 0 ? (
                <tr><td className="p-6 text-center text-[#8A796C]" colSpan={8}>No orders found.</td></tr>
              ) : (
                data.items.map((o) => (
                  <tr key={o._id} className="hover:bg-[#FAF8F5] transition-colors">
                    <td className="p-4 font-bold text-[#2B1810]">
                      <Link to={`/admin/orders/${o._id}`} className="hover:text-[#C49A45]">
                        {o.orderNumber}
                      </Link>
                    </td>
                    <td className="p-4 font-medium text-[#2B1810]">{o.user?.name || 'Customer'}</td>
                    <td className="p-4 text-[#8A796C]">{o.distanceKm ? `📍 ${o.distanceKm} km` : '—'}</td>
                    <td className="p-4 font-bold text-[#2B1810]">₹{o.totalAmount.toFixed(2)}</td>
                    <td className="p-4">
                      <span className="capitalize text-[#8A796C]">{o.paymentMethod} • <span className="font-bold text-[#2E5E3E]">{o.paymentStatus}</span></span>
                    </td>
                    <td className="p-4">
                      <select
                        value={o.orderStatus}
                        onChange={(e) => handleQuickStatusChange(o._id, e.target.value)}
                        className={`text-[11px] font-bold px-2 py-1 rounded border uppercase cursor-pointer focus:outline-none ${statusColor[o.orderStatus]}`}
                      >
                        {allStatuses.map((s) => (
                          <option key={s} value={s}>{s.toUpperCase()}</option>
                        ))}
                      </select>
                    </td>
                    <td className="p-4 text-[#8A796C]">{new Date(o.createdAt).toLocaleDateString()}</td>
                    <td className="p-4 text-right">
                      <Link 
                        to={`/admin/orders/${o._id}`} 
                        className="bg-[#FAF8F5] hover:bg-[#F5EFEB] border border-[#E8DFD8] text-[#2B1810] font-bold text-[11px] py-1.5 px-3 rounded transition-colors"
                      >
                        Details
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {data.pages > 1 && (
        <div className="flex gap-2">
          {Array.from({ length: data.pages }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              onClick={() => { const n = new URLSearchParams(searchParams); n.set('page', p); setSearchParams(n); }}
              className={`w-8 h-8 rounded text-xs font-bold ${p === page ? 'bg-[#C49A45] text-white' : 'bg-white border border-[#E8DFD8] text-[#2B1810]'}`}
            >
              {p}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
